/**
 * @file   src\Pages\CWOEditLocationBankInformation.tsx
 * @brief  Edit location ACH bank information for CWO.
 * @date   MAR, 2026
 */

import { React, useState, useEffect, Row, Col, useRef, Button } from '../utils/thirdpartyComponents';
import CWInput from '../components/CWInput';
import CWSelect from '../components/CWSelect';
import { AccountHolderType } from '../utils/enums';
import { ACCOUNT_NUMBER_MAX_LENGTH, ROUTING_NUMBER_MAX_LENGTH } from '../utils/constants';
import { cwoaddACHBankAccountSchema } from '../validations/cwoaddCarwashSchema';
import * as Yup from 'yup';
import { MessageToaster } from '../utils/toastUtil';
import { CompanyApi, UsersApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import { useStripe } from '@stripe/react-stripe-js';
import moment from "moment-timezone";
import { CompanyDetailsItem } from '../interfaces/generalInterface';
import SmallLoader from '../components/SmallLoader';

interface CWOEditLocationBankInformationProps {
    locationId: number;
    email?: string;
}

const CWOEditLocationBankInformation = ({ locationId, email = '' }: CWOEditLocationBankInformationProps) => {
    const stripe = useStripe();
    const toaster = new MessageToaster();
    const formRef = useRef<HTMLFormElement>(null);

    const [loading, setLoading] = useState(false);
    const [waitingForVerification, setWaitingForVerification] = useState(false);
    const [bankInformationVerificationStatus, setBankInformationVerificationStatus] = useState(true);
    const [bankInformationVerificationLink, setBankInformationVerificationLink] = useState<string | null>(null);
    const [companyDetails, setCompanyDetails] = useState<CompanyDetailsItem>({
        SubscriptionPaymentDate: null,
        SubscriptionStartDate: null,
        carWashGUID: '',
        carWashURL: '',
        hasUrlPermission: 0,
        subscriptionActive: 0
    });

    const [formData, setFormData] = useState({
        accountholdername: '',
        accountnumber: '',
        retypeaccountnumber: '',
        routingnumber: '',
        accountholdertype: 1,
    });

    const [errors, setErrors] = useState({
        accountholdername: '',
        accountnumber: '',
        retypeaccountnumber: '',
        routingnumber: '',
        accountholdertype: '',
    });

    const accountholderOptions = [
        { label: 'Select', value: 0 },
        ...Object.keys(AccountHolderType).filter((key) => !isNaN(Number(key))).map((key) => ({
            label: AccountHolderType[key as keyof typeof AccountHolderType],
            value: Number(key)
        }))
    ];

    const formatDate = (paymentDate: string) => {
        return moment(paymentDate).format('MM/DD/YYYY');
    };

    const getCompanyDetails = async () => {
        try {
            const { data } = await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);
            if (data?.error === 0 && data?.data) {
                setCompanyDetails(data.data);
            }
        } catch (error: any) {
            toaster.toastError(error?.response?.data?.message || error?.message || 'Failed to fetch company details.');
        }
    };

    const getLocationBank = async () => {
        if (!locationId) return;
        try {
            const { data } = await axios.get(`${UsersApi.CARWASH_OWNER_GET_BANK}/${locationId}`);
            if (data?.error === 0 && data?.data?.paymentDetails) {
                const ownerBank = data.data.paymentDetails;
                const accountHolderType = ownerBank.accountHolder === 'company' ? 2 : 1;
                setFormData({
                    accountholdername: ownerBank.billingName || '',
                    accountnumber: ownerBank.last4 ? `**** **** **** ${ownerBank.last4}` : '',
                    retypeaccountnumber: ownerBank.last4 ? `**** **** **** ${ownerBank.last4}` : '',
                    routingnumber: ownerBank.routingNumber || '',
                    accountholdertype: accountHolderType,
                });
            }
        } catch (error: any) {
            toaster.toastError(error?.response?.data?.message || error?.message || 'Failed to fetch bank details.');
        }
    };

    const checkBankInfoVerificationStatus = async () => {
        if (!locationId) return;
        try {
            setLoading(true);
            const verificationResult: any = await axios.get(`${UsersApi.CARWASH_OWNER_BANK_VERIFICATION_CHECKING}/${locationId}`);
            if (verificationResult?.data?.error === 0) {
                const status = verificationResult?.data?.data?.status;
                const nextActionType = verificationResult?.data?.data?.nextAction?.type;
                const verificationUrl = verificationResult?.data?.data?.nextAction?.verify_with_microdeposits?.hosted_verification_url || null;

                if (status === "requires_payment_method") {
                    setWaitingForVerification(false);
                    setBankInformationVerificationStatus(true);
                    setBankInformationVerificationLink(null);
                } else if (nextActionType === "verify_with_microdeposits") {
                    setBankInformationVerificationStatus(false);
                    setBankInformationVerificationLink(verificationUrl);
                } else if (status === "succeeded") {
                    setWaitingForVerification(false);
                    setBankInformationVerificationStatus(true);
                    setBankInformationVerificationLink(null);
                    getLocationBank();
                } else {
                    setWaitingForVerification(false);
                    setBankInformationVerificationStatus(true);
                    setBankInformationVerificationLink(null);
                }
            }
        } catch (error: any) {
            toaster.toastError(error?.response?.data?.message || error?.message || 'Failed to verify bank status.');
        } finally {
            setLoading(false);
        }
    };

    const handlepaymentSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption ? selectedOption.value : '';
        setFormData({
            ...formData,
            [name]: value,
        });
        if (errors[name as keyof typeof errors]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const onpaymentinfoInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.name === "routingnumber") {
            const routingValue = event.target.value;
            if (!/^\d*$/.test(routingValue)) {
                event.target.value = routingValue.replace(/\D/g, '');
            }
        }
        if (event.target.name === "accountnumber") {
            const accountValue = event.target.value;
            if (!/^\d*$/.test(accountValue)) {
                event.target.value = accountValue.replace(/\D/g, '');
            }
        }
        if (event.target.name === "retypeaccountnumber") {
            const retypeValue = event.target.value;
            if (!/^\d*$/.test(retypeValue)) {
                event.target.value = retypeValue.replace(/\D/g, '');
            }
        }

        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        if (errors[name as keyof typeof errors]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const updateCustomerPaymentMethod = async (paymentMethodId: string) => {
        const response = await axios.post(`${UsersApi.CARWASH_OWNER_MODIFY_BANK}/${locationId}`, {
            paymentMethod: paymentMethodId
        });
        if (response?.data?.error !== 0) {
            throw new Error(response?.data?.message || 'Unable to save bank account.');
        }
    };

    const attachBankAccountToStripe = async (clientSecret: string) => {
        if (!stripe) return null;
        return stripe.confirmUsBankAccountSetup(
            clientSecret,
            {
                payment_method: {
                    us_bank_account: {
                        account_number: formData.accountnumber,
                        routing_number: formData.routingnumber,
                        account_holder_type: formData.accountholdertype === 1 ? 'individual' : 'company',
                    },
                    billing_details: {
                        name: formData.accountholdername,
                        email
                    },
                },
            }
        );
    };

    const paymentinfohandleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!locationId) {
            toaster.toastError("Location details are not available.");
            return;
        }

        try {
            setLoading(true);
            await cwoaddACHBankAccountSchema.validate(formData, { abortEarly: false });

            if (!stripe) {
                toaster.toastError("Stripe is not initialized");
                return;
            }

            const responseClientSecret = await axios.get(`${UsersApi.CARWASH_OWNER_GET_CLIENT_SECRET}/${locationId}`);
            if (responseClientSecret?.data?.error !== 0) {
                toaster.toastError(responseClientSecret?.data?.message || "Unable to set up ACH bank account.");
                return;
            }

            const clientSecret = responseClientSecret.data.data.clientSecret;
            const setupIntentResult: any = await attachBankAccountToStripe(clientSecret);

            if (!setupIntentResult?.setupIntent?.id) {
                toaster.toastError(setupIntentResult?.error?.message || "Some error occurred. Please try again.");
                return;
            }

            if (setupIntentResult?.setupIntent?.status === "requires_payment_method") {
                setWaitingForVerification(false);
                toaster.toastError("Confirmation failed. Please try again with a different bank account.");
                setBankInformationVerificationStatus(true);
                setBankInformationVerificationLink(null);
                return;
            }

            if (setupIntentResult?.setupIntent?.status === "succeeded") {
                setWaitingForVerification(false);
                await updateCustomerPaymentMethod(setupIntentResult.setupIntent.payment_method);
                setBankInformationVerificationStatus(true);
                setBankInformationVerificationLink(null);
                await getLocationBank();
                toaster.toastSuccess("Bank information updated successfully.");
                return;
            }

            if (setupIntentResult?.setupIntent?.next_action?.type === "verify_with_microdeposits") {
                setBankInformationVerificationStatus(false);
                setBankInformationVerificationLink(setupIntentResult.setupIntent.next_action?.verify_with_microdeposits?.hosted_verification_url || null);
                toaster.toastSuccess("Bank account added. Please verify your account.");
                return;
            }

            toaster.toastError(setupIntentResult?.error?.message || "Some error occurred. Please try again.");
        } catch (error) {
            const newErrors: any = {};
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            } else {
                toaster.toastError((error as any)?.message || "Failed to update bank information.");
            }

            if (formRef.current && Object.keys(newErrors).length > 0) {
                const firstInvalidField = Object.keys(newErrors)[0];
                const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                if (invalidElement) {
                    invalidElement.focus();
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const openVerificationLink = (verificationLink: string) => {
        setWaitingForVerification(true);
        window.open(verificationLink, '_blank');
    };

    useEffect(() => {
        getCompanyDetails();
        getLocationBank();
        checkBankInfoVerificationStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationId]);

    useEffect(() => {
        if (bankInformationVerificationStatus === false) {
            const intervalId = setInterval(() => checkBankInfoVerificationStatus(), 60000);
            return () => clearInterval(intervalId);
        }
    }, [bankInformationVerificationStatus]);

    return (
        <form onSubmit={paymentinfohandleSubmit} ref={formRef}>
            <div className="cw-form-container">
                <div className="form-inner mx-auto">
                    <div className="payment-title d-flex align-items-center justify-content-between">
                        <h5 className='m-0'>Your Bank information is required for processing monthly payments via ACH to carwashapp.com</h5>
                        {(companyDetails && (companyDetails.SubscriptionPaymentDate || companyDetails.SubscriptionStartDate)) ? (
                            <div className="payment-label f-medium">
                                {companyDetails.SubscriptionPaymentDate === null
                                    ? companyDetails.SubscriptionStartDate && `Initial Payment Date : ${formatDate(companyDetails.SubscriptionStartDate)}`
                                    : companyDetails.SubscriptionPaymentDate && `Next Payment Date : ${formatDate(companyDetails.SubscriptionPaymentDate)}`}
                            </div>
                        ) : ''}
                    </div>

                    {(bankInformationVerificationStatus === false && bankInformationVerificationLink) ? (
                        <div className="payment-title d-flex align-items-center justify-content-between">
                            <p className='m-0'>
                                Your Bank information is not verified yet. Verify your account by entering the code on the secure verification page:
                                {' '}<a href="javascript:void(0)" onClick={() => openVerificationLink(bankInformationVerificationLink)}>Verify Now</a>
                            </p>
                        </div>
                    ) : ''}
                    {(bankInformationVerificationStatus === false && waitingForVerification) ? (
                        <div className="payment-title d-flex align-items-center justify-content-between">
                            <p className='m-0'>Waiting for bank verification. We will update this automatically.</p>
                        </div>
                    ) : ''}

                    {bankInformationVerificationStatus === true ? (
                        <>
                            <Row>
                                <Col md={6}>
                                    <CWSelect
                                        id="accountholdertype"
                                        name="accountholdertype"
                                        options={accountholderOptions}
                                        label="Select Account Holder Type"
                                        errorMessage={errors.accountholdertype}
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        values={accountholderOptions.filter(option => option.value === formData.accountholdertype)}
                                        onChange={(selectedOptions) => handlepaymentSelectChange(selectedOptions[0], 'accountholdertype')}
                                        placeholder="Select Account Holder Type"
                                        multi={false}
                                        searchable={false}
                                        isMandatory
                                    />
                                </Col>
                                <Col md={6}>
                                    <CWInput
                                        id="accountholdername"
                                        name="accountholdername"
                                        type="text"
                                        label="Account Name"
                                        value={formData.accountholdername}
                                        onChange={onpaymentinfoInputChange}
                                        errorMessage={errors.accountholdername}
                                        isMandatory
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <CWInput
                                        id="routingnumber"
                                        name="routingnumber"
                                        type="text"
                                        label="Routing Number"
                                        value={formData.routingnumber}
                                        onChange={onpaymentinfoInputChange}
                                        errorMessage={errors.routingnumber}
                                        maxLength={ROUTING_NUMBER_MAX_LENGTH}
                                        autoComplete="off"
                                        isMandatory
                                    />
                                </Col>
                                <Col md={6}>
                                    <CWInput
                                        id="accountnumber"
                                        name="accountnumber"
                                        type="text"
                                        label="Account Number"
                                        value={formData.accountnumber}
                                        onChange={onpaymentinfoInputChange}
                                        errorMessage={errors.accountnumber}
                                        maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                                        autoComplete="off"
                                        isMandatory
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <CWInput
                                        id="retypeaccountnumber"
                                        name="retypeaccountnumber"
                                        type="text"
                                        label="Re-Type Account Number"
                                        value={formData.retypeaccountnumber}
                                        onChange={onpaymentinfoInputChange}
                                        errorMessage={errors.retypeaccountnumber}
                                        maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                                        autoComplete="off"
                                        isMandatory
                                        onCopy={(e) => e.preventDefault()}
                                        onCut={(e) => e.preventDefault()}
                                        onPaste={(e) => e.preventDefault()}
                                    />
                                </Col>
                            </Row>
                            <div className="btn-sec d-flex justify-content-end">
                                <Button title="Update" variant="primary" className="primary-btn" type='submit' disabled={loading}>
                                    Update
                                </Button>
                            </div>
                        </>
                    ) : ''}
                </div>
            </div>
            {waitingForVerification && <SmallLoader />}
        </form>
    );
};

export default CWOEditLocationBankInformation;
